const moneyToCents = value => {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
};

const platformKey = value => {
  const key = String(value || 'Otros').trim().toLowerCase();
  return key === 'prime' ? 'prime video' : key;
};
const platformLabel = value => platformKey(value) === 'gpt' ? 'GPT' : String(value || 'Otros').trim();

export function buildProfitabilitySummary(transactions, inventory) {
  const accountsByPlatform = new Map();
  (Array.isArray(inventory) ? inventory : []).forEach(account => {
    const service = account.service || account.servicio || account.platform;
    const cost = account.cost ?? account.costo;
    const capacity = Number(account.capacity ?? account.capacidad);
    const costCents = moneyToCents(cost);
    if (!service || !Number.isInteger(capacity) || capacity <= 0 || costCents < 0) return;

    const key = platformKey(service);
    const current = accountsByPlatform.get(key) || { totalUnitCostCents: 0, count: 0, label: platformLabel(service) };
    current.totalUnitCostCents += costCents / capacity;
    current.count += 1;
    accountsByPlatform.set(key, current);
  });

  const groups = new Map();
  let totalProfitCents = 0;
  let assignedRevenueCents = 0;
  let missingCostCount = 0;
  let missingCostRevenueCents = 0;
  let estimatedCount = 0;

  (Array.isArray(transactions) ? transactions : []).forEach(transaction => {
    if (!['VENTA', 'RENOVACION'].includes(String(transaction.type || '').toUpperCase())) return;
    const key = platformKey(transaction.platform);
    const account = accountsByPlatform.get(key);
    const amountCents = moneyToCents(transaction.amount);
    const exactUnitCost = transaction.profile_unit_cost;
    let unitCostCents = null;
    let costSource = null;

    if (exactUnitCost !== null && exactUnitCost !== undefined && Number.isFinite(Number(exactUnitCost))) {
      unitCostCents = moneyToCents(exactUnitCost);
      costSource = 'linked_account';
    } else if (account?.count) {
      unitCostCents = Math.round(account.totalUnitCostCents / account.count);
      costSource = 'platform_average';
      estimatedCount += 1;
    }

    const label = account?.label || platformLabel(transaction.platform);
    const group = groups.get(key) || {
      platform: label,
      sales: 0,
      revenueCents: 0,
      allocatedCostCents: 0,
      knownProfitCents: 0,
      assignedRevenueCents: 0,
      exactCount: 0,
      estimatedCount: 0,
      missingCostCount: 0,
      missingCostRevenueCents: 0
    };

    group.sales += 1;
    group.revenueCents += amountCents;
    if (unitCostCents === null) {
      group.missingCostCount += 1;
      group.missingCostRevenueCents += amountCents;
      missingCostCount += 1;
      missingCostRevenueCents += amountCents;
    } else {
      group.allocatedCostCents += unitCostCents;
      group.knownProfitCents += amountCents - unitCostCents;
      group.assignedRevenueCents += amountCents;
      totalProfitCents += amountCents - unitCostCents;
      assignedRevenueCents += amountCents;
      if (costSource === 'linked_account') group.exactCount += 1;
      if (costSource === 'platform_average') group.estimatedCount += 1;
    }
    groups.set(key, group);
  });

  const platforms = Array.from(groups.values()).map(group => ({
    platform: group.platform,
    sales: group.sales,
    revenue: group.revenueCents / 100,
    allocatedCost: group.allocatedCostCents / 100,
    profit: group.missingCostCount ? null : group.knownProfitCents / 100,
    knownProfit: group.knownProfitCents / 100,
    assignedRevenue: group.assignedRevenueCents / 100,
    exactCount: group.exactCount,
    estimatedCount: group.estimatedCount,
    missingCostCount: group.missingCostCount,
    missingCostRevenue: group.missingCostRevenueCents / 100
  })).sort((a, b) => b.revenue - a.revenue);

  return {
    platforms,
    totalProfit: totalProfitCents / 100,
    assignedRevenue: assignedRevenueCents / 100,
    estimatedCount,
    missingCostCount,
    missingCostRevenue: missingCostRevenueCents / 100
  };
}
